<template>
	<div class="date-range-picker" ref="pickerRef" @keydown.esc.stop.prevent="onCancel">
		<div
			v-if="manualInput && !autoOpen"
			class="drp-manual-control"
			:class="{
				'drp-manual-control--active': hasValue,
				'drp-manual-control--invalid': manualInputInvalid,
			}"
		>
			<input
				:id="props.id || undefined"
				ref="manualInputRef"
				type="text"
				class="drp-manual-input"
				inputmode="numeric"
				autocomplete="off"
				:placeholder="manualInputPlaceholder"
				:disabled="disabled"
				:aria-invalid="manualInputInvalid ? 'true' : undefined"
				:value="manualInputText"
				@input="onManualInput"
				@blur="commitManualInput"
				@keydown.enter.prevent="onManualEnter"
				@keydown.escape.prevent="revertManualInput"
			/>
			<button
				type="button"
				class="drp-manual-toggle"
				:disabled="disabled"
				:aria-label="isOpen ? 'Close calendar' : 'Open calendar'"
				@click="toggleOpen"
			>
				<slot name="trigger-icon"><BsCalendar3 class="drp-trigger-icon" /></slot>
			</button>
		</div>
		<button
			v-else-if="!autoOpen"
			:id="props.id || undefined"
			type="button"
			class="drp-trigger"
			:class="{ 'drp-trigger--active': hasValue }"
			:disabled="disabled"
			:aria-expanded="isOpen"
			:aria-label="triggerLabel ? `${triggerLabel}: ${displayText}` : undefined"
			aria-haspopup="dialog"
			@click="toggleOpen"
		>
			<slot v-if="showTriggerIcon" name="trigger-icon"><BsCalendar3 class="drp-trigger-icon" /></slot>
			<span class="drp-trigger-text">{{ displayText }}</span>
		</button>

		<Transition name="drp-pop" :css="animated" :duration="{ enter: 300, leave: 150 }">
		<div
			v-if="isOpen"
			ref="dropdownRef"
			class="drp-dropdown"
			role="dialog"
			:aria-label="isSingleMode ? 'Choose date' : 'Choose dates'"
			:class="{ 'drp-dropdown--single': !isRangeMode, 'drp-dropdown--glass': glass }"
			:style="dropdownStyle"
			@mouseleave="hoverDate = null"
		>
			<div class="drp-body">
				<div class="drp-calendars">
				<!-- Left calendar (single mode: only calendar) -->
				<div class="drp-calendar">
					<div class="drp-cal-header">
						<button type="button" class="drp-nav-btn" aria-label="Previous month" @click="prevMonth">
							<slot name="previous-icon"><FlFilledChevronLeft /></slot>
						</button>
						<button
							v-if="canDrillDown && headerView !== 'years'"
							type="button"
							class="drp-month-title drp-month-title--clickable"
							@click="onHeaderTitleClick"
						>
							{{ headerTitleLabel }}
						</button>
						<span v-else class="drp-month-title">{{ canDrillDown ? headerTitleLabel : leftMonthLabel }}</span>
						<button v-if="!isRangeMode" type="button" class="drp-nav-btn" aria-label="Next month" @click="nextMonth">
							<slot name="next-icon"><FlFilledChevronRight /></slot>
						</button>
						<span v-else class="drp-nav-spacer" />
					</div>

					<!-- Month mode (prop-driven) -->
					<div v-if="isMonthMode" class="drp-month-grid">
						<button
							v-for="month in monthCells"
							:key="month.key"
							type="button"
							class="drp-month-cell"
							:class="{
								'drp-month-cell--selected': month.selected,
								'drp-month-cell--current': month.current,
							}"
							@click="onMonthClick(month)"
						>
							{{ month.label }}
						</button>
					</div>

					<!-- Year picker (drill-down) -->
					<div v-else-if="headerView === 'years'" class="drp-month-grid">
						<button
							v-for="cell in yearCells"
							:key="cell.key"
							type="button"
							class="drp-month-cell"
							:class="{
								'drp-month-cell--selected': cell.selected,
								'drp-month-cell--current': cell.current,
							}"
							@click="onNavYearSelect(cell)"
						>
							{{ cell.label }}
						</button>
					</div>

					<!-- Month picker (drill-down) -->
					<div v-else-if="headerView === 'months'" class="drp-month-grid">
						<button
							v-for="month in navMonthCells"
							:key="month.key"
							type="button"
							class="drp-month-cell"
							:class="{
								'drp-month-cell--selected': month.selected,
								'drp-month-cell--current': month.current,
							}"
							@click="onNavMonthSelect(month)"
						>
							{{ month.label }}
						</button>
					</div>

					<!-- Day grid (default) -->
					<template v-else>
						<div class="drp-cal-weekdays">
							<span v-for="d in dayLabels" :key="'lw-' + d" class="drp-weekday">{{ d }}</span>
						</div>
						<div class="drp-cal-grid">
							<button
								v-for="(cell, i) in leftCells"
								:key="'lc-' + i"
								type="button"
								:class="getCellClasses(cell)"
								:disabled="!cell.inMonth || exceedsMaxDate(cell.date)"
								:aria-label="cell.date ? format(cell.date, 'EEEE, d MMMM yyyy') : undefined"
								@click="onDateClick(cell)"
								@mouseenter="onDateHover(cell)"
							>
								<span v-if="cell.inMonth" class="drp-cell-num">{{ cell.day }}</span>
							</button>
						</div>
					</template>
				</div>

					<!-- Right calendar (range mode only) -->
					<div v-if="isRangeMode" class="drp-calendar">
						<div class="drp-cal-header">
							<span class="drp-nav-spacer" />
							<span class="drp-month-title">{{ rightMonthLabel }}</span>
							<button type="button" class="drp-nav-btn" aria-label="Next month" @click="nextMonth">
								<slot name="next-icon"><FlFilledChevronRight /></slot>
							</button>
						</div>
						<div class="drp-cal-weekdays">
							<span v-for="d in dayLabels" :key="'rw-' + d" class="drp-weekday">{{ d }}</span>
						</div>
						<div class="drp-cal-grid">
							<button
								v-for="(cell, i) in rightCells"
								:key="'rc-' + i"
								type="button"
								:class="getCellClasses(cell)"
								:disabled="!cell.inMonth"
								:aria-label="cell.date ? format(cell.date, 'EEEE, d MMMM yyyy') : undefined"
								@click="onDateClick(cell)"
								@mouseenter="onDateHover(cell)"
							>
								<span v-if="cell.inMonth" class="drp-cell-num">{{ cell.day }}</span>
							</button>
						</div>
					</div>
				</div>

				<!-- Presets sidebar (range mode only) -->
				<div v-if="isRangeMode" class="drp-presets">
					<button
						v-for="preset in presets"
						:key="preset.label"
						type="button"
						:class="['drp-preset-btn', { 'drp-preset-btn--active': isActivePreset(preset) }]"
						@click="onPresetClick(preset)"
					>
						{{ preset.label }}
					</button>
				</div>
			</div>

			<div class="drp-footer">
				<span class="drp-footer-range">{{ footerRangeText }}</span>
				<div class="drp-footer-actions">
					<button type="button" class="drp-btn drp-btn--cancel" @click="onCancel">Cancel</button>
					<button type="button" class="drp-btn drp-btn--update" :disabled="exceedsMaxDate(tempFrom)" @click="onUpdate">Update</button>
				</div>
			</div>
		</div>
		</Transition>
	</div>
</template>

<script setup>
import { fitFloatingPanel, trackFloatingAnchor, visibleViewport } from '@/utils/floatingPanel';
import { ref, computed, nextTick, onMounted, onUnmounted, onDeactivated, watch } from 'vue';
import {
	format,
	startOfMonth,
	endOfMonth,
	addMonths,
	subMonths,
	addYears,
	subYears,
	subDays,
	startOfQuarter,
	isSameDay,
	isSameMonth,
	isSameYear,
	isBefore,
	isAfter,
	getDay,
	getDaysInMonth,
	parseISO,
	startOfDay,
	isValid,
} from 'date-fns';
import {
	BsCalendar3,
	FlFilledChevronLeft,
	FlFilledChevronRight,
} from '@kalimahapps/vue-icons';

const props = defineProps({
	id: { type: String, default: '' },
	dateFrom: { type: String, default: '' },
	dateTo: { type: String, default: '' },
	// Optional upper bound for the compact single-date calendar.
	maxDate: { type: String, default: '' },
	glass: { type: Boolean, default: true },
	placeholder: { type: String, default: 'Date' },
	triggerLabel: { type: String, default: '' },
	showTriggerIcon: { type: Boolean, default: true },
	useFixed: { type: Boolean, default: false },
	// Opens the calendar with a short fade-and-grow from its trigger (fixed positioning only).
	animated: { type: Boolean, default: false },
	autoOpen: { type: Boolean, default: false },
	mode: { type: String, default: 'range' },
	disabled: { type: Boolean, default: false },
	manualInput: { type: Boolean, default: false },
	manualInputPlaceholder: { type: String, default: 'DD/MM/YYYY' },
});

const emit = defineEmits(['update', 'clear', 'close']);

const isSingleMode = computed(() => props.mode === 'single');
const isMonthMode = computed(() => props.mode === 'month');
const isRangeMode = computed(() => !isSingleMode.value && !isMonthMode.value);

const pickerRef = ref(null);
const dropdownRef = ref(null);
const manualInputRef = ref(null);
const isOpen = ref(false);
const dropdownStyle = ref({});
const baseMonth = ref(startOfMonth(new Date()));
const selecting = ref('start');
const tempFrom = ref(null);
const tempTo = ref(null);
const hoverDate = ref(null);
const headerView = ref('days');
const manualInputText = ref('');
const manualInputInvalid = ref(false);

const today = startOfDay(new Date());
const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/* ── Helpers ── */

const safeParseDate = (str) => {
	if (!str) return null;
	const d = parseISO(str);
	return isValid(d) ? startOfDay(d) : null;
};

const toISOString = (d) => (d ? format(d, 'yyyy-MM-dd') : '');
const maxSelectableDate = computed(() => safeParseDate(props.maxDate));
const exceedsMaxDate = (date) => Boolean(
	isSingleMode.value && date && maxSelectableDate.value && isAfter(date, maxSelectableDate.value),
);

const createValidatedDate = (year, month, day) => {
	const date = startOfDay(new Date(year, month - 1, day));
	if (
		date.getFullYear() !== year ||
		date.getMonth() !== month - 1 ||
		date.getDate() !== day
	) {
		return null;
	}
	return date;
};

const parseManualDateInput = (value) => {
	const trimmed = String(value || '').trim();
	if (!trimmed) return null;

	const isoLike = trimmed.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);
	if (isoLike) {
		const [, year, month, day] = isoLike.map(Number);
		return createValidatedDate(year, month, day);
	}

	const localLike = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
	if (localLike) {
		const [, day, month, year] = localLike.map(Number);
		return createValidatedDate(year, month, day);
	}

	return null;
};

const formatManualInputDate = (value) => {
	const date = typeof value === 'string' ? safeParseDate(value) : value;
	return date ? format(date, 'dd.MM.yyyy') : '';
};

const syncManualInputFromProps = () => {
	if (!props.manualInput) return;
	manualInputText.value = formatManualInputDate(props.dateFrom);
	manualInputInvalid.value = false;
};

const emitManualDate = (date) => {
	const isoDate = toISOString(date);
	tempFrom.value = date;
	tempTo.value = date;
	selecting.value = 'start';
	hoverDate.value = null;
	baseMonth.value = startOfMonth(date);
	emit('update', { dateFrom: isoDate, dateTo: isoDate });
};

const applyManualInputValue = (rawValue, { markInvalid = false } = {}) => {
	if (props.disabled) return true;

	const trimmed = String(rawValue || '').trim();
	if (!trimmed) {
		manualInputText.value = '';
		manualInputInvalid.value = false;
		tempFrom.value = null;
		tempTo.value = null;
		if (props.dateFrom || props.dateTo) {
			emit('update', { dateFrom: '', dateTo: '' });
		}
		return true;
	}

	const parsed = parseManualDateInput(trimmed);
	if (!parsed || exceedsMaxDate(parsed)) {
		manualInputInvalid.value = Boolean(markInvalid);
		return false;
	}

	manualInputInvalid.value = false;
	manualInputText.value = formatManualInputDate(parsed);
	emitManualDate(parsed);
	return true;
};

const onManualInput = (event) => {
	const rawValue = event?.target?.value || '';
	manualInputText.value = rawValue;
	manualInputInvalid.value = false;
	applyManualInputValue(rawValue, { markInvalid: false });
};

const commitManualInput = () => {
	applyManualInputValue(manualInputText.value, { markInvalid: true });
};

const onManualEnter = () => {
	const committed = applyManualInputValue(manualInputText.value, { markInvalid: true });
	if (committed) {
		manualInputRef.value?.blur();
	}
};

const revertManualInput = () => {
	syncManualInputFromProps();
	manualInputRef.value?.blur();
};

/* ── Display ── */

const hasValue = computed(() => Boolean(props.dateFrom || props.dateTo));

const displayText = computed(() => {
	const from = safeParseDate(props.dateFrom);
	const to = safeParseDate(props.dateTo);
	if (isMonthMode.value) {
		if (!from) return props.placeholder;
		return format(from, 'MMMM yyyy');
	}
	if (isSingleMode.value) {
		return from ? format(from, 'dd.MM.yyyy') : props.placeholder;
	}
	if (from && to) {
		if (isSameDay(from, to)) return format(from, 'MMM d, yyyy');
		return `${format(from, 'MMM d, yyyy')} \u2013 ${format(to, 'MMM d, yyyy')}`;
	}
	if (from) return format(from, 'MMM d, yyyy');
	return props.placeholder;
});

/* ── Calendar generation ── */

const generateCells = (monthDate) => {
	const cells = [];
	const first = startOfMonth(monthDate);
	const offset = (getDay(first) + 6) % 7;
	const total = getDaysInMonth(monthDate);

	for (let i = 0; i < offset; i++) {
		cells.push({ day: 0, date: null, inMonth: false });
	}
	for (let d = 1; d <= total; d++) {
		const date = new Date(first.getFullYear(), first.getMonth(), d);
		cells.push({ day: d, date: startOfDay(date), inMonth: true });
	}
	while (cells.length < 42) {
		cells.push({ day: 0, date: null, inMonth: false });
	}
	return cells;
};

const leftMonth = computed(() => baseMonth.value);
const rightMonth = computed(() => addMonths(baseMonth.value, 1));

const leftMonthLabel = computed(() =>
	isMonthMode.value ? format(leftMonth.value, 'yyyy') : format(leftMonth.value, 'MMMM yyyy'),
);
const rightMonthLabel = computed(() => format(rightMonth.value, 'MMMM yyyy'));

const leftCells = computed(() => generateCells(leftMonth.value));
const rightCells = computed(() => generateCells(rightMonth.value));
const monthCells = computed(() => {
	const year = leftMonth.value.getFullYear();
	return Array.from({ length: 12 }, (_, monthIndex) => {
		const monthDate = startOfMonth(new Date(year, monthIndex, 1));
		return {
			key: `${year}-${monthIndex}`,
			date: monthDate,
			label: format(monthDate, 'MMM'),
			selected: Boolean(tempFrom.value && isSameMonth(monthDate, tempFrom.value)),
			current: isSameMonth(monthDate, today),
		};
	});
});

/* ── Header drill-down (single/range modes) ── */

const canDrillDown = computed(() => !isMonthMode.value);

const headerTitleLabel = computed(() => {
	if (!canDrillDown.value) return leftMonthLabel.value;
	const year = leftMonth.value.getFullYear();
	if (headerView.value === 'years') {
		const decadeStart = year - (year % 12);
		return `${decadeStart} \u2013 ${decadeStart + 11}`;
	}
	if (headerView.value === 'months') return String(year);
	return format(leftMonth.value, 'MMMM yyyy');
});

const navMonthCells = computed(() => {
	const year = leftMonth.value.getFullYear();
	return Array.from({ length: 12 }, (_, i) => {
		const d = startOfMonth(new Date(year, i, 1));
		return {
			key: `nav-${year}-${i}`,
			date: d,
			label: format(d, 'MMM'),
			selected: isSameMonth(d, baseMonth.value),
			current: isSameMonth(d, today),
		};
	});
});

const yearCells = computed(() => {
	const year = leftMonth.value.getFullYear();
	const decadeStart = year - (year % 12);
	return Array.from({ length: 12 }, (_, i) => {
		const y = decadeStart + i;
		return {
			key: `year-${y}`,
			year: y,
			label: String(y),
			selected: y === baseMonth.value.getFullYear(),
			current: y === today.getFullYear(),
		};
	});
});

const onHeaderTitleClick = () => {
	if (props.disabled || !canDrillDown.value) return;
	if (headerView.value === 'days') {
		headerView.value = 'months';
	} else if (headerView.value === 'months') {
		headerView.value = 'years';
	}
};

const onNavMonthSelect = (month) => {
	if (props.disabled) return;
	baseMonth.value = startOfMonth(month.date);
	headerView.value = 'days';
};

const onNavYearSelect = (yearCell) => {
	if (props.disabled) return;
	const currentMonth = baseMonth.value.getMonth();
	baseMonth.value = startOfMonth(new Date(yearCell.year, currentMonth, 1));
	headerView.value = 'months';
};

/* ── Range highlighting ── */

const effectiveFrom = computed(() => {
	const f = tempFrom.value;
	const t = tempTo.value;
	const h = hoverDate.value;
	if (f && t) return isBefore(f, t) ? f : t;
	if (f && h && selecting.value === 'end') return isBefore(h, f) ? h : f;
	return f;
});

const effectiveTo = computed(() => {
	const f = tempFrom.value;
	const t = tempTo.value;
	const h = hoverDate.value;
	if (f && t) return isAfter(t, f) ? t : f;
	if (f && h && selecting.value === 'end') return isAfter(h, f) ? h : f;
	return t;
});

const getCellClasses = (cell) => {
	const cls = ['drp-cell'];
	if (!cell.inMonth) {
		cls.push('drp-cell--empty');
		return cls;
	}

	const d = cell.date;
	if (isSameDay(d, today)) cls.push('drp-cell--today');

	const f = effectiveFrom.value;
	const t = effectiveTo.value;
	const isStart = f && isSameDay(d, f);
	const isEnd = t && isSameDay(d, t);
	const inRange = f && t && isAfter(d, f) && isBefore(d, t);

	if (isStart) cls.push('drp-cell--selected', 'drp-cell--range-start');
	if (isEnd) cls.push('drp-cell--selected', 'drp-cell--range-end');
	if (inRange) cls.push('drp-cell--in-range');

	return cls;
};

/* ── Navigation ── */

const prevMonth = () => {
	if (props.disabled) return;
	if (headerView.value === 'years') {
		baseMonth.value = subYears(baseMonth.value, 12);
	} else if (headerView.value === 'months' || isMonthMode.value) {
		baseMonth.value = subYears(baseMonth.value, 1);
	} else {
		baseMonth.value = subMonths(baseMonth.value, 1);
	}
};
const nextMonth = () => {
	if (props.disabled) return;
	if (headerView.value === 'years') {
		baseMonth.value = addYears(baseMonth.value, 12);
	} else if (headerView.value === 'months' || isMonthMode.value) {
		baseMonth.value = addYears(baseMonth.value, 1);
	} else {
		baseMonth.value = addMonths(baseMonth.value, 1);
	}
};

/* ── Date selection ── */

const onDateClick = (cell) => {
	if (props.disabled) return;
	if (isMonthMode.value) return;
	if (!cell.inMonth || !cell.date || exceedsMaxDate(cell.date)) return;

	if (isSingleMode.value) {
		tempFrom.value = cell.date;
		tempTo.value = cell.date;
		return;
	}

	if (selecting.value === 'start' || tempTo.value !== null) {
		tempFrom.value = cell.date;
		tempTo.value = null;
		selecting.value = 'end';
	} else {
		if (isBefore(cell.date, tempFrom.value)) {
			tempTo.value = tempFrom.value;
			tempFrom.value = cell.date;
		} else {
			tempTo.value = cell.date;
		}
		selecting.value = 'start';
	}
};

const onMonthClick = (month) => {
	if (props.disabled) return;
	const monthDate = startOfMonth(month?.date || leftMonth.value);
	tempFrom.value = monthDate;
	tempTo.value = endOfMonth(monthDate);
	selecting.value = 'start';
	hoverDate.value = null;
};

const onDateHover = (cell) => {
	hoverDate.value = cell.inMonth && cell.date ? cell.date : null;
};

/* ── Presets ── */

const presets = computed(() => [
	{ label: 'Today', from: today, to: today },
	{ label: 'Yesterday', from: subDays(today, 1), to: subDays(today, 1) },
	{ label: 'Last 7 days', from: subDays(today, 6), to: today },
	{ label: 'Last 14 days', from: subDays(today, 13), to: today },
	{ label: 'Last 30 days', from: subDays(today, 29), to: today },
	{ label: 'This month', from: startOfMonth(today), to: today },
	{
		label: 'Last month',
		from: startOfMonth(subMonths(today, 1)),
		to: endOfMonth(subMonths(today, 1)),
	},
	{ label: 'This Quarter', from: startOfQuarter(today), to: today },
]);

const onPresetClick = (preset) => {
	if (props.disabled) return;
	tempFrom.value = preset.from;
	tempTo.value = preset.to;
	baseMonth.value = startOfMonth(preset.from);
	selecting.value = 'start';
};

const isActivePreset = (preset) => {
	if (!tempFrom.value || !tempTo.value) return false;
	return isSameDay(tempFrom.value, preset.from) && isSameDay(tempTo.value, preset.to);
};

/* ── Footer ── */

const footerRangeText = computed(() => {
	const f = tempFrom.value;
	const t = tempTo.value;
	if (isMonthMode.value) {
		return f ? format(f, 'MMMM yyyy') : '';
	}
	if (f && t) {
		if (isSameDay(f, t)) return format(f, 'MMM d, yyyy');
		return `${format(f, 'MMM d, yyyy')} \u2013 ${format(t, 'MMM d, yyyy')}`;
	}
	if (f) return format(f, 'MMM d, yyyy');
	return '';
});

/* ── Dropdown positioning ── */

// The opening motion grows out of the trigger, whichever side of it the panel lands on.
const openingOrigin = (style, triggerRect, height) => {
	const opensUp = parseFloat(style.top) < triggerRect.top;
	return {
		transformOrigin: `${triggerRect.left + triggerRect.width / 2 - parseFloat(style.left)}px ${opensUp ? height : 0}px`,
		'--drp-enter-y': opensUp ? '6px' : '-6px',
	};
};

const positionDropdown = async () => {
	if (props.useFixed) {
		const viewport = visibleViewport();
		dropdownStyle.value = {
			...dropdownStyle.value,
			width: undefined,
			maxWidth: `${Math.max(0, viewport.width - 16)}px`,
			maxHeight: `${Math.max(0, viewport.height - 16)}px`,
		};
	}
	await nextTick();
	const trigger = pickerRef.value;
	const dropdown = dropdownRef.value;
	if (!trigger || !dropdown || !trigger.isConnected) return;

	const triggerRect = trigger.getBoundingClientRect();
	const dropdownWidth = dropdown.offsetWidth;
	const dropdownHeight = dropdown.offsetHeight;
	const gap = 8;
	const viewportMargin = 8;
	const containerPadding = 12;
	const projectedBottom = triggerRect.bottom + gap + dropdownHeight;

	if (props.useFixed) {
		const style = fitFloatingPanel(triggerRect, {
			width: dropdownWidth, height: dropdownHeight,
		});
		dropdownStyle.value = props.animated ? { ...style, ...openingOrigin(style, triggerRect, dropdownHeight) } : style;
		return;
	}

	// ── Absolute positioning (default) ──

	// Find the overflow-clipping ancestor (.dashboard-content has overflow:hidden)
	const contentEl =
		trigger.closest('.dashboard-content') ||
		trigger.closest('.supplier-invoices-page');
	const contentRect = contentEl
		? contentEl.getBoundingClientRect()
		: {
			left: viewportMargin,
			right: window.innerWidth - viewportMargin,
			top: viewportMargin,
			bottom: window.innerHeight - viewportMargin,
		};
	const safeTop = Math.max(viewportMargin, contentRect.top + containerPadding);
	const safeBottom = Math.max(
		safeTop,
		Math.min(window.innerHeight - viewportMargin, contentRect.bottom - containerPadding),
	);
	const overflowBottom = Math.max(0, projectedBottom - safeBottom);

	// Default right-aligned: dropdown right edge = trigger right edge
	const defaultLeft = triggerRect.width - dropdownWidth;

	// Min: dropdown left edge must not go past content area left
	const minLeft = contentRect.left - triggerRect.left + gap;

	// Max: dropdown right edge must not go past content area right
	const maxLeft = contentRect.right - triggerRect.left - dropdownWidth - gap;

	const left = Math.min(Math.max(defaultLeft, minLeft), maxLeft);

	// Keep dropdown below trigger and shift up enough to clear the visible container bottom.
	const maxShiftUp = Math.max(0, triggerRect.bottom + gap - safeTop);
	const clampedShiftUp = Math.min(overflowBottom, maxShiftUp);

	dropdownStyle.value = {
		left: `${left}px`,
		right: 'auto',
		top: 'calc(100% + 8px)',
		bottom: 'auto',
		transform: `translateY(-${clampedShiftUp}px)`,
	};
};

/* ── Open / close / actions ── */

const toggleOpen = () => {
	if (props.disabled) return;
	if (isOpen.value) {
		isOpen.value = false;
		return;
	}
	tempFrom.value = safeParseDate(props.dateFrom);
	tempTo.value = safeParseDate(props.dateTo);
	selecting.value = 'start';
	hoverDate.value = null;
	headerView.value = 'days';

	if (tempFrom.value) {
		baseMonth.value = startOfMonth(tempFrom.value);
	} else {
		baseMonth.value = startOfMonth(today);
	}
	isOpen.value = true;
	positionDropdown();
};

const closePicker = () => {
	isOpen.value = false;
	emit('close');
};

const onCancel = () => {
	closePicker();
};

const onUpdate = () => {
	if (props.disabled || exceedsMaxDate(tempFrom.value)) return;
	if (isMonthMode.value) {
		const anchor = tempFrom.value || tempTo.value;
		if (!anchor) {
			emit('update', { dateFrom: '', dateTo: '' });
			closePicker();
			return;
		}
		const from = startOfMonth(anchor);
		const to = endOfMonth(anchor);
		emit('update', { dateFrom: toISOString(from), dateTo: toISOString(to) });
		closePicker();
		return;
	}
	const from = tempFrom.value;
	const to = tempTo.value || tempFrom.value;
	if (props.manualInput && isSingleMode.value) {
		manualInputText.value = formatManualInputDate(from);
		manualInputInvalid.value = false;
	}
	emit('update', { dateFrom: toISOString(from), dateTo: toISOString(to) });
	closePicker();
};

/* ── Click outside ── */

const onClickOutside = (e) => {
	if (isOpen.value && pickerRef.value && !pickerRef.value.contains(e.target)) {
		closePicker();
	}
};

/* ── Follow the trigger through filter animations and viewport changes ── */

let stopPositionTracking;
watch(isOpen, async (open) => {
	stopPositionTracking?.();
	stopPositionTracking = undefined;
	if (!open || !props.useFixed) return;
	await nextTick();
	if (isOpen.value && pickerRef.value?.isConnected) {
		stopPositionTracking?.();
		stopPositionTracking = trackFloatingAnchor(pickerRef.value, positionDropdown);
	}
});

/* ── Auto-open support ── */

const openPickerProgrammatically = () => {
	if (props.disabled) return;
	tempFrom.value = safeParseDate(props.dateFrom);
	tempTo.value = safeParseDate(props.dateTo);
	selecting.value = 'start';
	hoverDate.value = null;
	headerView.value = 'days';

	if (tempFrom.value) {
		baseMonth.value = startOfMonth(tempFrom.value);
	} else {
		baseMonth.value = startOfMonth(today);
	}
	isOpen.value = true;
	positionDropdown();
};

onMounted(() => {
	document.addEventListener('pointerdown', onClickOutside);
	if (props.autoOpen) {
		openPickerProgrammatically();
	}
});

onDeactivated(() => { if (isOpen.value) closePicker(); });

onUnmounted(() => {
	document.removeEventListener('pointerdown', onClickOutside);
	stopPositionTracking?.();
});

watch(
	() => props.disabled,
	(isDisabled) => {
		if (isDisabled && isOpen.value) {
			closePicker();
		}
	},
);

watch(
	() => [props.dateFrom, props.manualInput],
	syncManualInputFromProps,
	{ immediate: true },
);
</script>

<style scoped>
.date-range-picker {
	position: relative;
	display: inline-flex;
	align-items: center;
}

/* ── Trigger button ── */

.drp-trigger {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	height: 32px;
	padding: 0 18px;
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 20px;
	background: var(--noc-background, #fff);
	font-size: 0.9rem;
	font-family: inherit;
	cursor: pointer;
	white-space: nowrap;
	box-shadow: var(--box-shadow);
	transition: background-color 0.15s, border-color 0.15s;
}

.drp-trigger:hover {
	background: var(--noc-background-secondary, #fafafa);
}

.drp-trigger:disabled {
	cursor: not-allowed;
	opacity: 0.6;
}

.drp-trigger:disabled:hover {
	background: var(--noc-background, #fff);
}

.drp-trigger--active {
	border-color: var(--text-color, #333);
}

.drp-trigger-icon {
	flex-shrink: 0;
	font-size: 0.85rem;
	color: var(--text-color-secondary, #5e6170);
}

.drp-trigger-text {
	color: var(--text-color, #333);
}

.drp-manual-control {
	display: inline-flex;
	align-items: center;
	height: 32px;
	min-width: 150px;
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 20px;
	background: var(--noc-background, #fff);
	box-shadow: var(--box-shadow);
	transition: background-color 0.15s, border-color 0.15s;
}

.drp-manual-control:hover,
.drp-manual-control:focus-within {
	background: var(--noc-background-secondary, #fafafa);
}

.drp-manual-control:focus-within {
	border-color: var(--text-color, #333);
}

.drp-manual-control--active {
	border-color: var(--text-color, #333);
}

.drp-manual-control--invalid,
.drp-manual-control--invalid:focus-within {
	border-color: var(--noc-red, #d9534f);
}

.drp-manual-toggle {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 100%;
	padding: 0;
	border: none;
	background: transparent;
	color: inherit;
	cursor: pointer;
	flex-shrink: 0;
	font-family: inherit;
}

.drp-manual-toggle:disabled {
	cursor: not-allowed;
	opacity: 0.6;
}

.drp-manual-input {
	flex: 1 1 auto;
	min-width: 0;
	height: 100%;
	padding: 0 12px;
	border: 0;
	outline: 0;
	background: transparent;
	color: var(--text-color, #333);
	font: inherit;
	font-size: 0.9rem;
	box-sizing: border-box;
}

.drp-manual-input::placeholder {
	color: var(--text-color-secondary, #8a8f9d);
}

.drp-manual-input:disabled {
	cursor: not-allowed;
}

/* ── Dropdown panel ── */

.drp-dropdown {
	position: absolute;
	top: calc(100% + 8px);
	z-index: 1000;
	background: #fff;
	border: 1px solid #e2e2e2;
	border-radius: 14px;
	box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
	user-select: none;
}

.drp-dropdown--single .drp-body {
	padding: 16px 20px;
}

/* Opening motion (animated prop): the panel grows out of its trigger, then its content fades in.
   The content only fades: journal pages make the panel scrollable, and a moving child would flash a scrollbar. */
.drp-pop-enter-active {
	transition: opacity 180ms ease, transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.drp-pop-leave-active {
	transition: opacity 120ms ease, transform 140ms ease-in;
}

.drp-pop-enter-from,
.drp-pop-leave-to {
	opacity: 0;
	transform: translateY(var(--drp-enter-y, -6px)) scale(0.97);
}

.drp-pop-enter-active .drp-body,
.drp-pop-enter-active .drp-footer {
	transition: opacity 220ms ease 70ms;
}

.drp-pop-enter-from .drp-body,
.drp-pop-enter-from .drp-footer {
	opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
	.drp-pop-enter-active,
	.drp-pop-leave-active,
	.drp-pop-enter-active .drp-body,
	.drp-pop-enter-active .drp-footer {
		transition: none;
	}
}

@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
	.drp-dropdown--glass {
		background: rgba(255, 255, 255, 0.76);
		border-color: rgba(94, 97, 112, 0.18);
		-webkit-backdrop-filter: blur(18px) saturate(115%);
		backdrop-filter: blur(18px) saturate(115%);
		box-shadow: 0 8px 30px rgba(33, 38, 50, 0.1);
	}

	.drp-dropdown--glass .drp-footer {
		background: rgba(255, 255, 255, 0.55);
		border-color: rgba(94, 97, 112, 0.12);
		border-bottom-left-radius: inherit;
		border-bottom-right-radius: inherit;
	}

	.drp-dropdown--glass .drp-presets {
		border-color: rgba(94, 97, 112, 0.12);
	}
}

.drp-body {
	display: flex;
	padding: 20px 24px;
	gap: 20px;
}

/* ── Calendars ── */

.drp-calendars {
	display: flex;
	gap: 32px;
}

.drp-calendar {
	width: 280px;
}

.drp-cal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 12px;
	gap: 4px;
}

.drp-month-title {
	flex: 1;
	text-align: center;
	font-size: 0.95rem;
	font-weight: 600;
	color: #222;
	border: none;
	background: transparent;
	font-family: inherit;
}

.drp-month-title--clickable {
	cursor: pointer;
	border-radius: 8px;
	padding: 4px 8px;
	transition: background-color 0.12s ease;
}

.drp-month-title--clickable:hover {
	background: #f0f0f0;
}

.drp-nav-spacer {
	width: 30px;
	flex-shrink: 0;
}

.drp-nav-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	border: none;
	background: transparent;
	border-radius: 50%;
	cursor: pointer;
	color: #666;
	font-size: 1rem;
	flex-shrink: 0;
}

.drp-nav-btn:hover {
	background: #f0f0f0;
}

/* ── Weekday headers ── */

.drp-cal-weekdays {
	display: grid;
	grid-template-columns: repeat(7, 1fr);
}

.drp-weekday {
	text-align: center;
	font-size: 0.72rem;
	font-weight: 600;
	color: #999;
	padding: 6px 0;
}

/* ── Day grid ── */

.drp-cal-grid {
	display: grid;
	grid-template-columns: repeat(7, 1fr);
}

.drp-month-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 8px;
	padding-top: 8px;
}

.drp-month-cell {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 36px;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	background: #fff;
	color: #333;
	font-size: 0.82rem;
	font-weight: 500;
	font-family: inherit;
	cursor: pointer;
	transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.drp-month-cell:hover {
	background: #f5f5f5;
	border-color: #d1d5db;
}

.drp-month-cell--selected {
	background: var(--noc-blue, #0065ff);
	border-color: var(--noc-blue, #0065ff);
	color: #fff;
	font-weight: 600;
}

.drp-month-cell--current:not(.drp-month-cell--selected) {
	border-color: #9ca3af;
}

/* ── Day cells ── */

.drp-cell {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 36px;
	border: none;
	background: transparent;
	padding: 0;
	cursor: pointer;
	font-family: inherit;
}

.drp-cell--empty {
	pointer-events: none;
	cursor: default;
}

.drp-cell:disabled:not(.drp-cell--empty) {
	cursor: not-allowed;
}

.drp-cell-num {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	font-size: 0.82rem;
	color: #333;
	transition: background 0.1s;
	position: relative;
	z-index: 1;
}

/* Hover */
.drp-cell:not(:disabled):not(.drp-cell--empty):not(.drp-cell--selected):hover .drp-cell-num {
	background: #eaeaea;
}

/* Today */
.drp-cell--today:not(.drp-cell--selected) .drp-cell-num {
	font-weight: 800;
}

/* In range (between start and end) */
.drp-cell--in-range {
	background: #f2f2f2;
}

/* Range start — right half colored to connect to range */
.drp-cell--range-start:not(.drp-cell--range-end) {
	background: linear-gradient(to right, transparent 50%, #f2f2f2 50%);
}

/* Range end — left half colored to connect from range */
.drp-cell--range-end:not(.drp-cell--range-start) {
	background: linear-gradient(to left, transparent 50%, #f2f2f2 50%);
}

/* Single day selection (start == end) */
.drp-cell--range-start.drp-cell--range-end {
	background: transparent;
}

/* Selected circle */
.drp-cell--selected .drp-cell-num {
	background: var(--noc-blue, #0065ff);
	color: #fff;
	font-weight: 600;
}

/* ── Presets sidebar ── */

.drp-presets {
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 120px;
	border-left: 1px solid #ececec;
	padding-left: 20px;
	padding-top: 4px;
}

.drp-preset-btn {
	text-align: left;
	padding: 7px 12px;
	border: none;
	background: transparent;
	font-size: 0.82rem;
	color: #444;
	cursor: pointer;
	border-radius: 8px;
	white-space: nowrap;
	font-family: inherit;
	transition: background 0.1s;
}

.drp-preset-btn:hover {
	background: #f5f5f5;
	color: #111;
}

.drp-preset-btn--active {
	background: #f0f0f0;
	color: #111;
	font-weight: 600;
}

/* ── Footer ── */

.drp-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: #fff;
	padding: 14px 24px;
	border-top: 1px solid #ececec;
}

.drp-footer-range {
	font-size: 0.85rem;
	color: #666;
}

.drp-footer-actions {
	display: flex;
	gap: 8px;
}

.drp-btn {
	padding: 8px 22px;
	border-radius: 20px;
	font-size: 0.82rem;
	font-weight: 600;
	cursor: pointer;
	font-family: inherit;
	transition: background 0.15s;
}

.drp-btn--cancel {
	border: 1px solid #ddd;
	background: #fff;
	color: #555;
}

.drp-btn--cancel:hover {
	background: #f8f8f8;
}

.drp-btn--update {
	border: none;
	background: var(--noc-blue, #0065ff);
	color: #fff;
}

.drp-btn--update:hover {
	background: var(--noc-blue-hover, #0052cc);
}
</style>
